import { getOryFrontend } from '../ory/client';

export type ResetMode = 'popup' | 'self';

export interface ResetOptions {
  // Where to perform the logout flow. 'popup' opens a small window and polls Kratos; 'self' navigates current window.
  mode?: ResetMode;
  // Optional return_to target after logout. Defaults to `${origin}/` (root login page).
  returnTo?: string;
}

// Centralized reset that clears Hydra + Kratos sessions using the backend combined logout flow.
// It also clears local/session storage hints and best-effort non-HttpOnly cookies.
// When mode='popup' (default): opens a small popup, polls Kratos until session disappears, then resolves.
// When mode='self': navigates current window to the logout URL and returns a rejected promise to stop the caller's flow.
export async function resetAuthSession(options: ResetOptions = {}): Promise<void> {
  if (typeof window === 'undefined') return;
  const { mode = 'popup' } = options;
  const origin = window.location.origin;
  const returnTo = options.returnTo || `${origin}/`;

  // Clear local hints
  try {
    sessionStorage.removeItem('last_registered_email');
    sessionStorage.removeItem('last_flow_origin');
    // PKCE and OAuth state used by the FE during login
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('oauth_state');
    try {
      localStorage.removeItem('pkce_code_verifier_bak');
      localStorage.removeItem('oauth_state_bak');
    } catch {}
    // FE token storage keys
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('code_verifier');
    // Legacy/older keys we used in some flows
    localStorage.removeItem('hydra_pkce_verifier');
    localStorage.removeItem('hydra_pkce_challenge');
    localStorage.removeItem('ory_session');
  } catch {}
  // Best-effort cookie clear (HttpOnly will be cleared by server during logout)
  try {
    // Parse all cookies and clear likely auth-related ones, including dynamic csrf_token_* keys
    const raw = document.cookie || '';
    const pairs = raw.split(';').map((p) => p.trim());
    const toClear = new Set<string>();
    pairs.forEach((pair) => {
      const name = pair.split('=')[0];
      if (
        name.startsWith('csrf_token') ||
        name.startsWith('ory_kratos') ||
        name.startsWith('ory_hydra')
      ) {
        toClear.add(name);
      }
    });
    // Also include known fixed names for both dev and prod variants
    [
      'ory_kratos_session',
      'ory_kratos_continuity',
      'ory_kratos_session_dev',
      'ory_hydra_session',
      'ory_hydra_session_dev',
      'ory_hydra_login_csrf_dev',
      'ory_hydra_consent_csrf_dev',
    ].forEach((n) => toClear.add(n));

    toClear.forEach((name) => {
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    });
  } catch {}

  const logoutUrl = `/api/auth/combined-logout?return_to=${encodeURIComponent(returnTo)}`;

  if (mode === 'self') {
    // Navigate current window and stop the caller flow.
    window.location.href = logoutUrl;
    throw new Error('RedirectingForLogout');
  }

  // Default 'popup' mode
  const ory = getOryFrontend();
  const popup = window.open(
    logoutUrl,
    'auth_logout_reset',
    'width=460,height=640,menubar=no,toolbar=no,location=no,status=no'
  );
  if (!popup) {
    // Popup blocked: full redirect and stop the caller flow
    window.location.href = logoutUrl;
    throw new Error('RedirectingForLogout');
  }

  // Poll session until gone
  await new Promise<void>((resolve) => {
    let attempts = 0;
    const maxAttempts = 30; // ~12s
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        await ory.toSession();
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          try {
            popup.close();
          } catch {}
          resolve();
        }
      } catch {
        // Session is gone
        clearInterval(interval);
        try {
          popup.close();
        } catch {}
        resolve();
      }
    }, 400);
  });

  // After logout completes (or times out), navigate main window to the desired login/root page
  try {
    window.location.replace(returnTo);
  } catch {
    // no-op
  }
}
